import { Send, Trash2 } from '@/components/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { LIMITS } from '@clanfire/shared';
import { IconButton } from '@/components/ui/Button';
import { toast } from '@/stores/ui';

/** How many bars a voice message's waveform has. */
export const WAVEFORM_BARS = 64;

export function voiceRecordingSupported(): boolean {
  return typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/** The best format this browser records in: Opus in WebM (Chrome, Edge) or Ogg (Firefox), else MP4 (Safari). */
function recordingFormat(): { mimeType?: string; ext: string } {
  const options: Array<[string, string]> = [
    ['audio/webm;codecs=opus', 'webm'],
    ['audio/ogg;codecs=opus', 'ogg'],
    ['audio/mp4', 'm4a'],
    ['audio/webm', 'webm'],
  ];
  for (const [mimeType, ext] of options) if (MediaRecorder.isTypeSupported?.(mimeType)) return { mimeType, ext };
  return { ext: 'webm' };
}

/** Squeezes loudness samples (0–1) into `bars` bytes, scaled so the loudest bar is full height, as base64. */
export function encodeWaveform(levels: number[], bars = WAVEFORM_BARS): string {
  const bytes = new Uint8Array(bars);
  if (levels.length) {
    const peak = Math.max(...levels, 0.01);
    for (let i = 0; i < bars; i++) {
      const from = Math.floor((i * levels.length) / bars);
      const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / bars));
      const slice = levels.slice(from, to);
      const avg = slice.reduce((a, b) => a + b, 0) / slice.length;
      bytes[i] = Math.round(Math.min(1, avg / peak) * 255);
    }
  }
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Records a voice message as soon as it appears: a timer and a live waveform,
 * with buttons to bin it or send it. Escape bins it too. Stops (and sends) by
 * itself at the 5-minute limit.
 */
export function VoiceRecorder({ onSend, onCancel }: { onSend: (file: File, voice: { durationMs: number; waveform: string }) => void; onCancel: () => void }) {
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const [ready, setReady] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const finish = useRef<'send' | 'cancel'>('cancel');
  const startedAt = useRef(0);
  const allLevels = useRef<number[]>([]);
  const callbacks = useRef({ onSend, onCancel });
  callbacks.current = { onSend, onCancel };

  useEffect(() => {
    let stream: MediaStream | null = null;
    let audio: AudioContext | null = null;
    let timer = 0;
    let cancelled = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      } catch {
        toast.error('Allow microphone access in your browser to record a voice message.');
        return callbacks.current.onCancel();
      }
      if (cancelled) return stream.getTracks().forEach((t) => t.stop());

      // Loudness, ten times a second, for the waveform.
      audio = new AudioContext();
      const analyser = audio.createAnalyser();
      analyser.fftSize = 512;
      audio.createMediaStreamSource(stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);

      const format = recordingFormat();
      const media = new MediaRecorder(stream, format.mimeType ? { mimeType: format.mimeType, audioBitsPerSecond: 48_000 } : undefined);
      const chunks: Blob[] = [];
      media.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      media.onstop = () => {
        const durationMs = Date.now() - startedAt.current;
        stream?.getTracks().forEach((t) => t.stop());
        void audio?.close();
        if (finish.current !== 'send') return callbacks.current.onCancel();
        if (durationMs < 500) {
          toast.info('That was too short to send.');
          return callbacks.current.onCancel();
        }
        const type = media.mimeType || format.mimeType || 'audio/webm';
        const file = new File(chunks, `voice-message.${format.ext}`, { type: type.split(';')[0] });
        callbacks.current.onSend(file, { durationMs: Math.min(durationMs, LIMITS.voiceMessageMaxMs), waveform: encodeWaveform(allLevels.current) });
      };
      media.start(250);
      recorder.current = media;
      startedAt.current = Date.now();
      setReady(true);

      timer = window.setInterval(() => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const s of samples) sum += s * s;
        const level = Math.min(1, Math.sqrt(sum / samples.length) * 4);
        allLevels.current.push(level);
        setLevels((l) => [...l.slice(-39), level]);
        const ms = Date.now() - startedAt.current;
        setElapsed(ms);
        if (ms >= LIMITS.voiceMessageMaxMs) {
          finish.current = 'send';
          if (media.state !== 'inactive') media.stop();
        }
      }, 100);
    })();

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
      else {
        stream?.getTracks().forEach((t) => t.stop());
        void audio?.close();
      }
    };
  }, []);

  const stop = (how: 'send' | 'cancel') => {
    finish.current = how;
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
    else if (how === 'cancel') onCancel();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        stop('cancel');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex items-center gap-2 p-2" role="group" aria-label="Recording a voice message">
      <IconButton label="Delete recording" tone="danger" onClick={() => stop('cancel')}>
        <Trash2 size={18} />
      </IconButton>
      <span className="flex items-center gap-2 text-sm font-semibold text-danger tabular-nums" aria-live="off">
        <span className={ready ? 'h-2.5 w-2.5 animate-pulse rounded-full bg-danger' : 'h-2.5 w-2.5 rounded-full bg-fg-muted'} aria-hidden />
        {formatDuration(elapsed)}
      </span>
      <div className="flex h-9 min-w-0 flex-1 items-center justify-end gap-[3px] overflow-hidden" aria-hidden>
        {levels.map((level, i) => (
          <span key={i} className="w-[3px] shrink-0 rounded-full bg-accent" style={{ height: `${Math.max(12, level * 100)}%` }} />
        ))}
      </div>
      <span className="hidden text-xs text-fg-muted sm:inline">{ready ? 'Recording…' : 'Starting…'}</span>
      <button
        type="button"
        onClick={() => stop('send')}
        disabled={!ready}
        aria-label="Send voice message"
        className="flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-full bg-accent font-display text-sm text-accent-fg transition-[background-color,opacity] hover:bg-accent-hover disabled:opacity-45 sm:w-auto sm:px-4"
      >
        <span className="hidden sm:inline">Send</span>
        <Send size={16} />
      </button>
    </div>
  );
}
